import { initI18n, t } from './i18n';
import { escapeHtml } from './sanitize';

initI18n();

const $ = (id: string) => document.getElementById(id)!;

function formatUptime(totalSeconds: number) {
  const d = Math.floor(totalSeconds / 86400);
  const h = Math.floor((totalSeconds % 86400) / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return { d, h, m, s };
}

function getDayPhase(time: number) {
  if (time >= 0.2 && time < 0.35) return { icon: '\u{1F305}', text: t('stats.sunrise') };
  if (time >= 0.35 && time < 0.65) return { icon: '\u2600\uFE0F', text: t('stats.day') };
  if (time >= 0.65 && time < 0.8)  return { icon: '\u{1F307}', text: t('stats.sunset') };
  return { icon: '\u{1F319}', text: t('stats.night') };
}

// --- Sparkline chart system ---
const MAX_POINTS = 60; // 2 minutes of data at 2s intervals

interface ChartSeries {
  data: number[];
  color: string;
  label: string;
}

interface ChartState {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  series: ChartSeries[];
}

const charts: Record<string, ChartState> = {};

function initChart(canvasId: string, seriesDefs: { color: string; label: string }[]): ChartState {
  const canvas = $(canvasId) as HTMLCanvasElement;
  const ctx = canvas.getContext('2d')!;

  // HiDPI support
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);

  const series = seriesDefs.map(s => ({
    data: [] as number[],
    color: s.color,
    label: s.label,
  }));

  const state: ChartState = { canvas, ctx, series };
  charts[canvasId] = state;
  return state;
}

function pushData(canvasId: string, values: number[]) {
  const chart = charts[canvasId];
  if (!chart) return;
  for (let i = 0; i < chart.series.length; i++) {
    chart.series[i].data.push(values[i] ?? 0);
    if (chart.series[i].data.length > MAX_POINTS) {
      chart.series[i].data.shift();
    }
  }
  drawChart(chart);
}

function drawChart(chart: ChartState) {
  const { ctx, canvas, series } = chart;
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.width / dpr;
  const h = canvas.height / dpr;

  // Clear
  ctx.clearRect(0, 0, w, h);

  // Background
  ctx.fillStyle = '#3a2718';
  ctx.fillRect(0, 0, w, h);

  // Grid lines
  ctx.strokeStyle = 'rgba(255,255,255,0.06)';
  ctx.lineWidth = 1;
  for (let i = 1; i < 4; i++) {
    const y = (h / 4) * i;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }

  // Find global max across all series
  let max = 0;
  for (const s of series) {
    for (const v of s.data) {
      if (v > max) max = v;
    }
  }
  max = max || 1;
  // Add 10% headroom
  max *= 1.1;

  const padding = 2;
  const graphH = h - padding * 2;
  const graphW = w - padding * 2;

  for (const s of series) {
    if (s.data.length < 2) continue;

    const step = graphW / (MAX_POINTS - 1);

    // Fill area
    ctx.beginPath();
    ctx.moveTo(padding + (MAX_POINTS - s.data.length) * step, h - padding);
    for (let i = 0; i < s.data.length; i++) {
      const x = padding + (MAX_POINTS - s.data.length + i) * step;
      const y = h - padding - (s.data[i] / max) * graphH;
      ctx.lineTo(x, y);
    }
    ctx.lineTo(padding + (MAX_POINTS - 1) * step, h - padding);
    ctx.closePath();
    ctx.fillStyle = s.color + '18'; // very transparent fill
    ctx.fill();

    // Line
    ctx.beginPath();
    for (let i = 0; i < s.data.length; i++) {
      const x = padding + (MAX_POINTS - s.data.length + i) * step;
      const y = h - padding - (s.data[i] / max) * graphH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = s.color;
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.stroke();

    // Dot at current value
    if (s.data.length > 0) {
      const lastI = s.data.length - 1;
      const x = padding + (MAX_POINTS - 1) * step;
      const y = h - padding - (s.data[lastI] / max) * graphH;
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fillStyle = s.color;
      ctx.fill();
    }
  }

  // Max label
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.font = '9px "Press Start 2P", monospace';
  ctx.textAlign = 'right';
  ctx.fillText(String(Math.round(max / 1.1)), w - 4, 12);
}

// Init charts on load
let memChart: ChartState;
let wsChart: ChartState;

function initCharts() {
  memChart = initChart('chart-memory', [
    { color: '#5cba47', label: 'RSS' },
    { color: '#e67e22', label: 'Heap' },
  ]);
  wsChart = initChart('chart-ws', [
    { color: '#3498db', label: 'In' },
    { color: '#e74c3c', label: 'Out' },
  ]);
}

// Handle resize
let resizeTimer: ReturnType<typeof setTimeout>;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    // Re-init canvases with new dimensions
    for (const [id, chart] of Object.entries(charts)) {
      const dpr = window.devicePixelRatio || 1;
      const rect = chart.canvas.getBoundingClientRect();
      chart.canvas.width = rect.width * dpr;
      chart.canvas.height = rect.height * dpr;
      chart.ctx.scale(dpr, dpr);
      drawChart(chart);
    }
  }, 200);
});

function update(data: any) {
  // Players
  $('player-count').textContent = data.players.count;
  const list = $('player-list');
  if (data.players.names.length === 0) {
    list.innerHTML = `<li class="empty-msg">${t('stats.noPlayers')}</li>`;
  } else {
    list.innerHTML = data.players.names
      .map((n: string) => `<li><span class="player-icon"></span>${escapeHtml(n)}</li>`)
      .join('');
  }

  // Uptime
  const up = formatUptime(data.uptime);
  $('up-d').textContent = String(up.d);
  $('up-h').textContent = String(up.h);
  $('up-m').textContent = String(up.m);
  $('up-s').textContent = String(up.s);

  // Worlds (with per-world day/night)
  const worldsList = $('worlds-list');
  if (data.worlds && data.worlds.length > 0) {
    worldsList.innerHTML = data.worlds.map((w: any) => {
      const phase = getDayPhase(w.dayTime ?? 0.3);
      return `<div class="world-row">
        <span class="world-name">${w.name}</span>
        <span class="world-info">
          <span class="world-daynight"><span class="world-daynight-icon">${phase.icon}</span>${phase.text}</span>
          <span class="world-players">${t('stats.worldPlayers', { count: w.playerCount, max: w.maxPlayers })}</span>
        </span>
      </div>`;
    }).join('');
  } else {
    worldsList.innerHTML = `<div class="empty-msg">${t('stats.noPlayers')}</div>`;
  }

  // Metrics
  if (data.metrics) {
    const m = data.metrics;

    // Performance stats
    $('loaded-chunks').textContent = String(m.loadedChunks);
    $('loaded-worlds').textContent = String(m.loadedWorlds);

    // Memory chart
    pushData('chart-memory', [m.memory.rss, m.memory.heapUsed]);
    $('mem-rss').textContent = m.memory.rss + ' MB';
    $('mem-heap').textContent = m.memory.heapUsed + ' MB';

    // WebSocket chart
    pushData('chart-ws', [m.wsMessages.inPerSec, m.wsMessages.outPerSec]);
    $('ws-in').textContent = String(m.wsMessages.inPerSec);
    $('ws-out').textContent = String(m.wsMessages.outPerSec);
  }
}

// SSE connection
let retryDelay = 1000;

function connect() {
  const source = new EventSource('/stats/stream');

  source.onmessage = (e) => {
    retryDelay = 1000;
    $('status-dot').classList.remove('offline');
    try {
      update(JSON.parse(e.data));
    } catch {}
  };

  source.onerror = () => {
    $('status-dot').classList.add('offline');
    source.close();
    setTimeout(connect, retryDelay);
    retryDelay = Math.min(retryDelay * 2, 10000);
  };
}

initCharts();
connect();
